





import { Component, Vue } from 'vue-property-decorator';
import { Dictionary } from 'vue-router/types/router';
import cssVars from 'css-vars-ponyfill';
import defaultTheme from '@/plugins/themes/defaultTheme';
import Theme from '@/models/Theme';
import { namespace } from 'vuex-class';

const ApplicationStore = namespace('application');
const AuthStore = namespace('auth');
const MagazineStore = namespace('magazine');

/**
 * First view that is used in application (except App.vue of course). Decides if the code view shoulb be displayed
 * and handles the theming.
 */
@Component({})
export default class EntryView extends Vue {

    @ApplicationStore.Action('loadThemeAction')
    private loadThemeAction!: (themeToken: string) => Promise<Theme>;

    @ApplicationStore.Mutation('setCustomTheme')
    private setCustomTheme!: (theme: Theme | undefined) => void;

    @ApplicationStore.Getter('customTheme')
    private customTheme!: Theme;

    @ApplicationStore.Mutation('setNoAnimations')
    private setNoAnimations!: (value: boolean) => void;

    @MagazineStore.Mutation('setPagination')
    private setPagination!: (value: number | null) => void;

    @AuthStore.Getter('token')
    private token!: string;

    /**
     * Url Params for ident Token, theme token usw.
     */
    public queryParams?: Dictionary<string | (string | null)[]>;

    /**
     * The theming background image
     */
    public backgroundImage: { background?: string } = {};

    public created() {
        // get url params
        this.queryParams = { ...this.$route.query };
        this.handleNoAnimationsParam();
        this.handlePaginationParam();
        // get a nice theming!
        this.handleTheming();
    }

    /**
     * Handles the theming
     */
    private async handleTheming() {
        // The url contains the theme token -> Load and apply theme
        if (!this.queryParams?.theme && !this.customTheme) {
            // No themeToken found -> Load the default theme
            this.applyDefaultTheme();
        } else {
            try {
                // the theme should be loaded only when the token changes
                if (this.queryParams?.theme && (!this.customTheme?.token || this.customTheme?.token !== this.queryParams.theme)) {
                    await this.loadThemeAction(this.queryParams!.theme as string);
                }
                this.activateThemeColors(this.customTheme);

                // handle background
                if (this.customTheme.backgroundPath) {
                    // check if background is image
                    if (/.*(\.png|\.svg)/.test(this.customTheme.backgroundPath)) {
                        this.backgroundImage = {
                            background: `url(${require(`@/assets/themes/${this.customTheme.token}/${this.customTheme.backgroundPath}`)})`
                        };
                    } else {
                        // otherwise it is a color value
                        this.backgroundImage = {
                            background: `${this.customTheme.backgroundPath} !important`
                        };
                    }
                }

                // handle additional styles
                if (this.customTheme.customStyles) {
                    const styleLink = document.createElement('link');
                    styleLink.rel = 'stylesheet';
                    styleLink.href = `assets/themes/${this.customTheme.token}/style.css`;
                    document.head.appendChild(styleLink);
                }
            } catch (e) {
                // if some error occurs, use default theme
                console.info(`theme ${this.queryParams?.theme} could not be used. Use default theme instead.`);
                this.applyDefaultTheme();
            }
        }
        // cssVars applies theming to legacy browser
        // for further information take a look at:
        // https://jhildenbiddle.github.io/css-vars-ponyfill/#/
        Vue.nextTick(() => cssVars({
            silent: false // default
        }));
    }

    /**
     *  A function that applies the default theme
     */
    private applyDefaultTheme() {
        this.$vuetify.theme.themes.light = defaultTheme as any; // any cast is need because some value are not defined
        this.setCustomTheme(undefined);
        this.backgroundImage = {
            background: 'url( ' + require('@/assets/themes/default/background.svg') + ') '
        };
    }

    /**
     * Overites theme color
     * @param theme
     */
    private activateThemeColors(theme: Theme) {
        const currentTheme = this.$vuetify.theme.themes.light;
        Object.keys(currentTheme).forEach(key => {
            if (theme[key]) { // use only available theme colors
                currentTheme[key] = theme[key] as string;
            }
        });
    }

    /**
     * Check for noAnimations query param
     */
    private handleNoAnimationsParam() {
        if (this.queryParams && 'noAnimations' in this.queryParams!) {
            if (this.queryParams.noAnimations === 'true' || this.queryParams.noAnimations === null) {
                this.setNoAnimations(true);
            } else {
                this.setNoAnimations(false);
            }
        }
    }

    /**
     * Check for pagination query param
     */
    private handlePaginationParam() {
        if (this.queryParams && 'pagination' in this.queryParams!) {
            const paginationNumber = Number(this.queryParams.pagination);
            if (this.queryParams.pagination === null) {
                this.setPagination(Number(process.env.VUE_APP_DEFAULT_PAGINATION_VALUE));
            } else if (!isNaN(paginationNumber)) {
                this.setPagination(paginationNumber);
            } else {
                this.setPagination(null);
            }
        }
    }
}
